import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DetectMethodChanges, DetectSetterChanges } from '../utils';
import { OrionSettingsService } from './orion-settings.service';
import { OrionSettingsDto } from '../shell-communication/shell-operations-protocol';
import { WEEKDAYS, Weekday } from '../adjustment-pricing-grid/copilot/apg-copilot-domain';

// One box of the "Weekdays to exclude" group: label above, checkbox below (owner sketch,
// 2026-09-15). Checked = excluded by default.
export interface WeekdayExclusionBox {
   id: Weekday;
   text: string;
   checked: boolean;
}

export function weekdayExclusionBoxes(checked: Weekday[]): WeekdayExclusionBox[] {
   return WEEKDAYS.map(wd => ({ id: wd, text: wd, checked: checked.indexOf(wd) >= 0 }));
}

// CR-20260904-001, ORION Settings follow-on (2026-09-08).
//
// The "ORION Settings" host-menu dialog: SHELL-level settings that govern every ORION on the shell.
// Super-user-only on the surface (the menu item is hidden from everyone else) and on the wire (the
// Shell refuses a non-super-user save).
//
// LAYOUT: one column per ORION, read top to bottom. Only APG ORION has settings today, so only its
// column renders, full width. Hedging Grid ORION and OPG ORION columns are added beside it when they
// gain settings (owner's stated intent).
@Component({
   selector: 'ets-orion-settings-dialog',
   templateUrl: './orion-settings-dialog.component.html',
   styleUrls: ['./orion-settings-dialog.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrionSettingsDialogComponent {
   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _orionSettingsService: OrionSettingsService,
      private readonly _toastr: ToastrService
   ) { }

   title = 'ORION Settings';

   private _isVisible = false;
   get isVisible(): boolean {
      return this._isVisible;
   }
   @DetectSetterChanges()
   set isVisible(v: boolean) {
      this._isVisible = v;
   }

   private _isLoading = false;
   get isLoading(): boolean {
      return this._isLoading;
   }
   @DetectSetterChanges()
   set isLoading(v: boolean) {
      this._isLoading = v;
   }

   // The editable copy. Re-read from the service every time the dialog opens, so a Close without
   // Save discards edits and a save by ANOTHER super user (delivered by the next init) is not
   // overwritten by stale values sitting in this component.
   apgShowTopN: number = null;
   apgMaxQueryRangeDays: number = null;

   // CR-20260915-002: "Weekdays to exclude" - the APG ORION's STARTING weekday-exclusion set
   // (default Mon). Five boxes, Mon..Fri; checked = excluded by default. An EMPTY set is allowed
   // (owner ruling 4). Held RESOLVED, like the numbers: the super user sees what the date block
   // actually starts with.
   apgWeekdayExclusions: WeekdayExclusionBox[] = weekdayExclusionBoxes([]);

   // Maven's shell-level settings share the same record (OrionSettingsDto) but NOT this
   // dialog: Maven is not an ORION, so they have their own "Maven Settings" host-menu item and
   // dialog (MavenSettingsDialogComponent, 2026-09-14). This dialog never reads or writes them -
   // Maven's OWN "Weekdays to exclude" included.

   @DetectMethodChanges()
   onShown(): void {
      const s = this._orionSettingsService.get();
      this.apgShowTopN = s.apgShowTopN;
      this.apgMaxQueryRangeDays = s.apgMaxQueryRangeDays;
      this.apgWeekdayExclusions = weekdayExclusionBoxes(this._orionSettingsService.apgWeekdayExclusions);
   }

   @DetectMethodChanges()
   onWeekdayChanged(box: WeekdayExclusionBox, e: { value?: boolean }): void {
      box.checked = !!(e && e.value);
   }

   @DetectMethodChanges()
   onHidden(): void {
      this.isVisible = false;
   }

   // No floor, no ceiling (owner ruling 2026-09-08). The only refusal is an EMPTY box: an empty
   // cap has no meaning, and storing it would make every ORION run fall back to a default the
   // super user never chose.
   async onSaveClicked(): Promise<void> {
      if (!this.isWholeNumber(this.apgShowTopN)) {
         this._toastr.error('"Show Top N Results" Must be a Whole Number', 'APG ORION');
         return;
      }
      if (!this.isWholeNumber(this.apgMaxQueryRangeDays)) {
         this._toastr.error('"Max Query Range (Days)" Must be a Whole Number', 'APG ORION');
         return;
      }

      // The Maven fields are carried through AS HELD: this dialog does not own them, and an
      // ORION save must not overwrite what the MAVEN Settings dialog stored.
      const settings: OrionSettingsDto = {
         ...this._orionSettingsService.get(),
         apgShowTopN: this.apgShowTopN,
         apgMaxQueryRangeDays: this.apgMaxQueryRangeDays,
         // The checked boxes, possibly NONE: an empty array is stored as the decision it is.
         apgWeekdayExclusions: this.apgWeekdayExclusions.filter(b => b.checked).map(b => b.id)
      };

      try {
         this.isLoading = true;
         await this._orionSettingsService.save(settings);
         this._toastr.success('ORION Settings Saved');
      } catch (e) {
         this._toastr.error('"Save ORION Settings" Operation Completed With Errors');
      } finally {
         this.isLoading = false;
      }
   }

   @DetectMethodChanges()
   onCloseClicked(): void {
      this.onHidden();
   }

   private isWholeNumber(v: any): boolean {
      return typeof v === 'number' && isFinite(v) && Math.trunc(v) === v;
   }
}
